import { motion } from 'framer-motion';
import { useState } from 'react';
import '../styles/TechStack.css';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1], delay },
});

const itemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      delay: i * 0.05,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const techCategories = [
  {
    name: 'Cloud',
    technologies: [
      { name: 'AWS', icon: '/assets/icons/AWS.svg' },
      { name: 'Azure', icon: '/assets/icons/Azure.svg' },
      { name: 'GCP', icon: '/assets/icons/Google Cloud.svg' },
      { name: 'Oracle Cloud', icon: '/assets/icons/Oracle.svg' },
    ],
  },
  {
    name: 'Data & AI',
    technologies: [
      { name: 'Apache Spark', icon: '/assets/icons/Apache Spark.svg' },
      { name: 'Snowflake', icon: '/assets/icons/snowflake-color.svg' },
      { name: 'Hadoop', icon: '/assets/icons/Apache Hadoop.svg' },
      { name: 'Redis', icon: '/assets/icons/Redis.svg' },
    ],
  },
  {
    name: 'Frontend',
    technologies: [
      { name: 'React', icon: '/assets/icons/React.svg' },
      { name: 'Angular', icon: '/assets/icons/Angular.svg' },
      { name: 'Vue.js', icon: '/assets/icons/Vue.js.svg' },
      { name: 'Flutter', icon: '/assets/icons/Flutter.svg' },
      { name: 'Swift', icon: '/assets/icons/Swift.svg' },
    ],
  },
  {
    name: 'Backend',
    technologies: [
      { name: 'Node.js', icon: '/assets/icons/Node.js.svg' },
      { name: 'Python', icon: '/assets/icons/Python.svg' },
      { name: 'Java', icon: '/assets/icons/Java.svg' },
      { name: '.NET', icon: '/assets/icons/NET core.svg' },
      { name: 'Go', icon: '/assets/icons/Go.svg' },
      { name: 'Ruby', icon: '/assets/icons/Ruby.svg' },
    ],
  },
  {
    name: 'DevOps',
    technologies: [
      { name: 'Docker', icon: '/assets/icons/Docker.svg' },
      { name: 'Kubernetes', icon: '/assets/icons/Kubernetes.svg' },
      { name: 'Jenkins', icon: '/assets/icons/Jenkins.svg' },
      { name: 'GitLab CI/CD', icon: '/assets/icons/GitLab.svg' },
      { name: 'Terraform', icon: '/assets/icons/Terraform.svg' },
    ],
  },
  {
    name: 'Database',
    technologies: [
      { name: 'PostgreSQL', icon: '/assets/icons/PostgresSQL.svg' },
      { name: 'MySQL', icon: '/assets/icons/MySQL.svg' },
      { name: 'MongoDB', icon: '/assets/icons/MongoDB.svg' },
      { name: 'Elasticsearch', icon: '/assets/icons/Elastic Search.svg' },
    ],
  },
];

function TechItem({ tech }) {
  return (
    <motion.div
      className="tech-item"
      custom={0}
      variants={itemVariants}
      whileHover={{ y: -6 }}
    >
      <div className="tech-item__icon-container">
        <img src={tech.icon} alt={tech.name} className="tech-item__icon" />
      </div>
      <span className="tech-item__name">{tech.name}</span>
    </motion.div>
  );
}

function TechCategory({ category, isActive, onClick }) {
  return (
    <motion.button
      className={`tech-tab ${isActive ? 'active' : ''}`}
      onClick={onClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <span className="tech-tab__name">{category.name}</span>
    </motion.button>
  );
}

export default function TechStack() {
  const [activeTab, setActiveTab] = useState(0);
  const activeCategory = techCategories[activeTab];

  return (
    <section className="section tech-stack">
      <div className="container">
        <motion.div className="tech__header" {...fadeUp(0)}>
          <div className="section__tag">Technology</div>
          <h2 className="section__title">Our Tech Stack</h2>
          <p className="tech__subtitle">Enterprise-grade technologies powering scalable solutions</p>
        </motion.div>

        <div className="tech-stack__content">
          {/* Tab Navigation */}
          <motion.div className="tech-tabs" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            {techCategories.map((category, idx) => (
              <TechCategory
                key={category.name}
                category={category}
                isActive={activeTab === idx}
                onClick={() => setActiveTab(idx)}
              />
            ))}
          </motion.div>

          {/* Active Category Content */}
          <motion.div
            className="tech-grid"
            key={activeCategory.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            {activeCategory.technologies.map((tech, idx) => (
              <motion.div key={tech.name} custom={idx} variants={itemVariants} initial="hidden" animate="visible">
                <TechItem tech={tech} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
